import axios, { AxiosError, AxiosRequestConfig } from "axios";
import axiosRetry from "axios-retry";
import { v4 as uuidv4 } from "uuid";
// import { addError } from "src/store/errorBoundarySlice";
import store from "../store/store";
import { tokenManager } from "src/services/tokenManager";
import { deviceDetectionService } from "src/services/deviceDetectionService";
import { DeviceType } from "src/types/deviceFlowTypes";

const TOKEN_EXPIRED_MESSAGE =
  "The security token included in the request is expired";

const apiClient = axios.create({
  baseURL: "https://prod.na.safety-genie-api.whs.amazon.dev",
});

/**
 * Enhanced HTTP interceptor for dual token support
 */
class AuthInterceptor {
  private deviceType: DeviceType;

  constructor() {
    this.deviceType = deviceDetectionService.getDeviceType();
  }

  /**
   * Add authentication headers based on device type and token type
   * Requirements 4.1, 4.2 - Include appropriate headers/parameters based on device type
   */
  addAuthHeaders = async (config: AxiosRequestConfig): Promise<AxiosRequestConfig> => {
    try {
      // Determine authentication type from stored tokens
      const authType = tokenManager.getStoredAuthType();

      if (!authType) {
        console.log('No authentication type found, skipping auth headers');
        return config;
      }

      // Check if we actually have tokens for this auth type
      const existingTokens = tokenManager.getTokens(authType);
      if (!existingTokens) {
        console.log(`No ${authType} tokens found, skipping auth headers`);
        return config;
      }

      // Ensure we have valid tokens before making the request
      // Requirements 5.1, 5.2 - Proactive token refresh (before API calls, not after 4xx errors)
      let tokens;
      try {
        tokens = await tokenManager.ensureValidTokens(authType);
      } catch (error) {
        console.error('Failed to ensure valid tokens:', error);

        // If we're on mobile and trying to get federate tokens but they don't exist,
        // this likely means the user hasn't authenticated yet
        if (authType === 'federate' && this.deviceType === 'mobile') {
          console.log('No federate tokens found for mobile device - user may need to authenticate');
        }

        // If token validation/refresh fails, proceed without auth headers
        // The backend will return 401/403 and we'll handle it in the response interceptor
        return config;
      }

      // Initialize headers if not present
      if (!config.headers) {
        config.headers = {};
      }

      // Add client request ID for tracking
      config.headers.clientRequestId = uuidv4();
      // For FormData requests (e.g. file uploads), let the browser set Content-Type
      // automatically so it includes the correct multipart boundary string.
      if (!(config.data instanceof FormData)) {
        config.headers['Content-Type'] = 'application/json';
      }

      if (authType === 'federate' && this.deviceType === 'mobile') {
        // For mobile devices with federate tokens, use ONLY query parameters (no headers)
        // Requirement 4.1 - Include federate tokens as query parameters for mobile requests

        if (!config.params) {
          config.params = {};
        }

        // Add federate tokens as query parameters only
        config.params.access_token = tokens.accessToken;
        config.params.id_token = tokens.idToken;

        // Do NOT add Authorization or id_token headers for mobile devices

      } else if (authType === 'cognito' || this.deviceType === 'desktop') {
        // For desktop devices or Cognito tokens, use existing header format
        // Requirement 4.3 - Maintain existing Cognito header format for desktop requests

        // Use the access token or ID token for authorization
        const authToken = tokens.accessToken || tokens.idToken;
        config.headers.authorization = `Bearer ${authToken}`;

        // For backward compatibility, also set the legacy token header if using Cognito
        if (authType === 'cognito') {
          // Check if there's a legacy token in localStorage (used by existing authService)
          const legacyToken = localStorage.getItem('token');
          if (legacyToken) {
            config.headers.authorization = `Bearer ${legacyToken}`;
          }
        }
      }

      console.log(`Auth headers added for ${authType} authentication on ${this.deviceType} device`);
      return config;

    } catch (error) {
      console.error('Failed to add auth headers:', error);
      // Return config without auth headers rather than failing the request
      return config;
    }
  };

  /**
   * Handle authentication errors - no automatic token refresh or redirects
   * Just log and throw the error for components to handle
   */
  handleAuthError = async (error: AxiosError): Promise<any> => {
    const status = error.response?.status;
    const message: string = error.response?.data?.message ?? "";

    console.log(`Auth error received: ${status} - ${message}`);
    console.log('Not attempting token refresh - tokens should be refreshed proactively before API calls');

    // Don't attempt any token refresh on 4xx errors
    // Token refresh should happen proactively before API calls, not reactively after failures
    throw error;
  };

  /**
   * Retry request with refreshed authentication
   */
  retryWithRefresh = async (originalRequest: AxiosRequestConfig): Promise<any> => {
    try {
      console.log('Retrying request with refreshed tokens');

      // Add fresh auth headers to the retry request
      const configWithAuth = await this.addAuthHeaders(originalRequest);

      // Make the retry request
      return await apiClient(configWithAuth);

    } catch (error) {
      console.error('Retry with refresh failed:', error);
      throw error;
    }
  };
}

// Create interceptor instance
const authInterceptor = new AuthInterceptor();

// Add request interceptor for authentication headers
apiClient.interceptors.request.use(
  authInterceptor.addAuthHeaders,
  (error) => {
    console.error('Request interceptor error:', error);
    return Promise.reject(error);
  }
);

// Enhanced retry configuration with dual token support
axiosRetry(apiClient, {
  retries: 1,
  retryDelay: (retryCount) => {
    console.log(`retry attempt: ${retryCount}`);
    return retryCount * 2000;
  },
  retryCondition: (error: AxiosError) => {
    console.log("retryCondition err: ", JSON.stringify(error));
    const status = error.response?.status;
    const message: string = error.response?.data?.message ?? "";

    // Handle authentication errors through our auth interceptor
    if (status === 401 || (status === 403 && message.includes(TOKEN_EXPIRED_MESSAGE))) {
      // Let the response interceptor handle auth errors
      return false;
    }

    // for any other types of 403, typically for authenticated but unauthorized Amazon users, no need to retry
    // for everything else, retry for now till further observation
    return !!status && status !== 403;
  },
});

// Enhanced response interceptor with dual token support
apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    // Handle authentication errors first
    const status = error.response?.status;
    if (status === 401 || status === 403) {
      try {
        return await authInterceptor.handleAuthError(error);
      } catch (authError) {
        // If auth error handling fails, continue with normal error handling
        console.error('Auth error handling failed:', authError);
      }
    }

    // Handle other errors (non-auth)
    if (error.response?.status !== 429) {
      if (error.response?.data?.message) {
        // store.dispatch(addError(error.response?.data?.message));
      } else {
        // store.dispatch(addError(error?.message));
      }
    }

    return Promise.reject(error);
  }
);

export default apiClient;

// Export the auth interceptor for testing purposes
export { authInterceptor };
